import { appConfig } from 'appConfig';
import axios, { AxiosInstance, AxiosError } from 'axios';
import { getItem } from './localstorage';
import { SESSION_KEY, IMPERSONATE_SESSION_KEY } from './constants';
import {
  SubscriptionExpiredError,
  isRequestBlockedBySubscription,
} from './subscriptionGate';

const PUBLIC_ROUTES = ['/login/', '/register/', '/forgot_password/'];

class ApiService {
  session: AxiosInstance;

  constructor() {
    this.session = axios.create();

    this.session.interceptors.request.use((axiosConfig) => {
      axiosConfig.baseURL = appConfig.apiBaseUrl;
      axiosConfig.withCredentials = appConfig.useCookieAuth;

      if (
        isRequestBlockedBySubscription(axiosConfig?.method, axiosConfig?.url)
      ) {
        return Promise.reject(
          new SubscriptionExpiredError()
        ) as unknown as typeof axiosConfig;
      }

      if (PUBLIC_ROUTES.some((s) => axiosConfig?.url?.endsWith(s))) {
        return {
          ...axiosConfig,
          headers: {
            ...axiosConfig.headers,
            'X-Platform-Version': 'light',
          },
        };
      }

      const sid = getItem(IMPERSONATE_SESSION_KEY) || getItem(SESSION_KEY);
      if (!sid) {
        return {
          ...axiosConfig,
          headers: {
            ...axiosConfig.headers,
            'X-Platform-Version': 'light',
          },
        };
      }

      return {
        ...axiosConfig,
        headers: {
          ...axiosConfig.headers,
          'X-Platform-Version': 'light',
          Authorization: `Bearer ${sid}`,
        },
      };
    });

    // Add response interceptor to handle errors globally
    this.session.interceptors.response.use(
      (response) => response,
      (error: AxiosError) => {
        // Handle network errors or server timeouts
        if (!error.response) {
          // Network error, server is unreachable
          const networkError = new Error(
            'Network Error: Unable to connect to the server'
          );
          networkError.name = 'NetworkError';
          return Promise.reject(networkError);
        }

        // Server responded with an error status
        return Promise.reject(error);
      }
    );
  }

  updateContentType = (contentType: string) => {
    this.session.defaults.headers.common['Content-Type'] = contentType;
  };

  async get(url: string, params: Record<string, any> = {}) {
    return this.session.get(url, { params });
  }

  async getBlob(
    url: string,
    params: Record<string, any> = {},
    headers: Record<string, string> = {}
  ) {
    return this.session.get(url, { params, headers, responseType: 'blob' });
  }

  async post(
    url: string,
    payload: Record<string, any> = {},
    q: Record<string, any> = {}
  ) {
    return this.session.post(url, payload, { params: q });
  }

  async put(
    url: string,
    payload: Record<string, any> = {},
    q: Record<string, any> = {}
  ) {
    return this.session.put(url, payload, { params: q });
  }

  async patch(
    url: string,
    payload: Record<string, any> = {},
    q: Record<string, any> = {}
  ) {
    return this.session.patch(url, payload, { params: q });
  }

  async delete(url: string, q: Record<string, any> = {}) {
    return this.session.delete(url, { params: q });
  }
}

export default new ApiService();
